import { useState, useEffect, useMemo } from "react";
import { LOYALTY_API_BASE } from "../constants";
import { loyaltyFetch } from "./loyaltyFetch";
import { enrichConditionFieldCatalog } from "../utils/conditionFieldUtils";

async function fetchTransactionTypeDetail(brandName, transactionTypeKey) {
    const listRes = await loyaltyFetch(
        `${LOYALTY_API_BASE}/admin/transaction-types?brand=${encodeURIComponent(brandName)}`,
        { headers: { "X-Brand": brandName } },
    );
    if (!listRes.ok) return null;
    const list = await listRes.json().catch(() => []);
    const summary = Array.isArray(list) ? list.find((t) => t.key === transactionTypeKey) : null;
    if (!summary?.id) return null;
    const detailRes = await loyaltyFetch(
        `${LOYALTY_API_BASE}/admin/transaction-types/${summary.id}`,
        { headers: { "X-Brand": brandName } },
    );
    if (!detailRes.ok) return null;
    return detailRes.json().catch(() => null);
}

function mergeConditionCatalog(fieldsData, txDetail) {
    if (!fieldsData && !txDetail) return null;
    const base = fieldsData && typeof fieldsData === "object" ? fieldsData : {};
    const merged = {
        ...base,
        payload_schema: txDetail?.payload_schema ?? base.payload_schema,
        payloadFields: txDetail?.payload_fields ?? base.payloadFields ?? base.payload_fields,
        ruleHints: base.ruleHints ?? txDetail?.rule_hints ?? txDetail?.ruleHints ?? null,
    };
    return enrichConditionFieldCatalog(merged);
}

/**
 * Catalogue condition-fields pour un type de transaction (règles).
 * GET /admin/rules/ui-options/condition-fields?transaction_type=...
 */
export function useRuleConditionFields(brandName, transactionType) {
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!brandName || !transactionType) {
            setCatalog(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const url = `${LOYALTY_API_BASE}/admin/rules/ui-options/condition-fields?transaction_type=${encodeURIComponent(transactionType)}`;
        Promise.all([
            loyaltyFetch(url, { headers: { "X-Brand": brandName } }).then((r) => (r.ok ? r.json() : null)),
            fetchTransactionTypeDetail(brandName, transactionType),
        ])
            .then(([fieldsData, txDetail]) => {
                if (!cancelled) setCatalog(mergeConditionCatalog(fieldsData, txDetail));
            })
            .catch(() => { if (!cancelled) setCatalog(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [brandName, transactionType]);

    const payloadFields = useMemo(() => {
        if (Array.isArray(catalog?.payloadFields)) return catalog.payloadFields;
        return [];
    }, [catalog]);

    const payloadPaths = useMemo(() => {
        if (payloadFields.length > 0) {
            return payloadFields.map((f) => f.name || f.earnPointsPath).filter(Boolean);
        }
        const fromSources = catalog?.sources?.payload;
        if (Array.isArray(fromSources)) {
            return fromSources.map((p) => String(p).replace(/^payload\./, ""));
        }
        return [];
    }, [catalog, payloadFields]);

    const hasPayloadSchema = useMemo(() => payloadPaths.length > 0, [payloadPaths]);

    return {
        catalog,
        loading,
        ruleHints: catalog?.ruleHints || null,
        payloadFields,
        payloadPaths,
        hasPayloadSchema,
        fieldMeta: catalog?.fieldMeta || {},
        valuePresets: catalog?.valuePresets || {},
    };
}

export { fetchTransactionTypeDetail, mergeConditionCatalog };
